import { Box, Button, Link, Stack, Text } from "@vinomofo/components";
import { determineTierIndex, isUserAStaff } from "../../utils/loyalty";
import { useCallback, useEffect, useState } from "react";

import { COLORS } from "../../styles";
import { Link as Route } from "gatsby";
import { UserIcon } from "@vinomofo/icons";
import { VinoUser } from "../../contexts/auth/state";
import { navigate } from "gatsby";
import styled from "styled-components";
import { toTitleCase } from "../../utils/string-helpers";
import { trackEvent } from "../../utils/track-event";
import useAuth from "../../hooks/use-auth";
import { useCommunicationPreferences } from "../../hooks/account/use-communication-preferences";
import { useLocation } from "@reach/router";
import { useLoyalty } from "../../hooks/account/use-loyalty";

export interface HeaderAuthProps {
  isLarge?: boolean;
}

const GREETINGS = [
  "Aloha",
  "Hello",
  "Hi",
  "もしもし",
  "¡Buenos Días",
  "Yo",
  "Sup",
  "Ahoy-hoy",
  "Guten Tag",
  "Good Day",
  "¡Hola",
  "Bonjour",
  "Namaste",
  "Salam",
  "Salut",
  "Ellohay",
  "Oh Hello",
  "Buongiorno",
  "Ciao",
  "Ué ué",
];

const getRandomGreeting = () =>
  GREETINGS[Math.floor(Math.random() * GREETINGS.length)];

const Container = styled(Text)`
  font-weight: 500;
  align-self: center;
`;

const AuthLink = styled(Link)`
  text-decoration: none;
  font-size: inherit;

  &:hover {
    text-decoration: none;
    color: ${({ theme }) => theme.colors.brand};
  }
`;

const IconLink = styled(Button)`
  display: inline-flex;
  text-decoration: none;

  pointer-events: ${({ isLoading }) => (isLoading ? "none" : "auto")};
  opacity: ${({ isLoading }) => (isLoading ? 0.5 : 1)};
  transition: opacity ease-in-out 500ms;

  &:hover {
    color: ${({ theme }) => theme.colors.brand};
    svg {
      color: white; /* making sure SVG from hover color to white */
    }
  }
`;

const StyledBox = styled(Box)`
  opacity: ${({ isLoading }) => (isLoading ? 0 : 1)};
  transition: opacity ease-in-out 500ms;
`;

export const HeaderAuth = ({
  isLarge = false,
}: HeaderAuthProps): JSX.Element => {
  const { isAuthenticated, user, login, join, isLoading } = useAuth();
  const location = useLocation();
  const [tierName, setTierName] = useState("");
  const {
    communicationPreferences: userProfile,
  } = useCommunicationPreferences();
  const { loyalty, showLoyalty } = useLoyalty();
  const { tier: tierKey } = determineTierIndex(
    user as VinoUser,
    showLoyalty,
    loyalty?.loyalty_tier
  );

  useEffect(() => {
    if (showLoyalty) localStorage.setItem("eligibleForLoyalty", "true");
    else localStorage.removeItem("eligibleForLoyalty");
  }, [showLoyalty]);

  const handleLogin = useCallback(async () => {
    trackEvent("Login Clicked", {
      button: "Login",
      location: location?.pathname ?? "/",
    });
    login({ returnTo: location?.pathname ?? "/" });
  }, [login]);

  const handleSignUp = useCallback(async () => {
    trackEvent("Join Clicked", {
      button: "Join",
      location: location?.pathname ?? "/",
    });
    join({ returnTo: location?.pathname ?? "/" });
  }, [join]);

  // State to store the current greeting
  const [greeting, setGreeting] = useState(getRandomGreeting());

  useEffect(() => {
    if (user && isUserAStaff(user)) {
      setTierName(user?.customer_group?.name)
    } else {
      setTierName(loyalty?.loyalty_tier)
    }
  }, [user, loyalty])

  useEffect(() => {
    // Function to update the greeting every 1 minute
    const intervalId = setInterval(() => {
      setGreeting(getRandomGreeting());
    }, 10000); // 60 seconds

    // Cleanup the interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  const AuthenticatedGreeting = () => {
    return (
      <Container forwardedAs="div" fontSize={[0, 1]} color="white" align="right">
        <AuthLink
          as={Route}
          to={"/account"}
          color="white"
          fontSize={[0, 1]}
          fontWeight="500"
        >
          {greeting}{" "}
          {userProfile?.firstname || user?.given_name || "Mofo"}!
        </AuthLink>
        <Box display={"flex"} justifyContent={"end"}>
          <AuthLink
            as={Route}
            to={"/account/very-important-mofo"}
            color="white"
            fontSize={[0, 1]}
            fontWeight="500"
          >
            {tierName}
          </AuthLink>
        </Box>
      </Container>
    );
  };

  const UnauthenticatedLinks = () => {
    return (
      <Container
        forwardedAs="div"
        color="white"
        fontFamily={"normal"}
        fontSize={[0, 1]}
        fontWeight="500"
      >
        <Button
          onClick={handleLogin}
          color="white"
          fontFamily={"normal"}
          fontSize={[0, 1]}
          fontWeight="500"
        >
          Login
        </Button>
        {" / "}
        <Button
          onClick={handleSignUp}
          color="white"
          fontFamily={"normal"}
          fontSize={[0, 1]}
          fontWeight="500"
        >
          Join
        </Button>
      </Container>
    );
  };

  const AuthDetail = (
    <StyledBox display="flex" alignItems="center" isLoading={isLoading}>
      {isAuthenticated ? <AuthenticatedGreeting /> : <UnauthenticatedLinks />};
    </StyledBox>
  );

  return (
    <Stack space={1} horizontal>
      {isLarge && AuthDetail}

      <IconLink
        onClick={() => {
          isAuthenticated ? navigate("/account") : handleLogin();
        }}
        color="white"
        disabled={isLoading}
      >
        <Box
          style={{
            height: 40,
            width: 40,
            borderRadius: 25,
            backgroundColor:
              showLoyalty && tierKey
                ? COLORS[
                    `tier${toTitleCase(tierKey === "none" ? "green" : tierKey)}`
                  ]
                : "transparent",
            padding: "8px 9px",
          }}
          color="white"
        >
          <UserIcon size="22" color="white" />
        </Box>
      </IconLink>
    </Stack>
  );
};
